import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';
import { AJMAN_DISTRICTS } from '../data/ajmanDistricts';
import { loadGoogleMapsAPI } from '../utils/googleMapsLoader';
import { GoSnelMap } from '../components/common/GoSnelMap';

// Add spinner animation
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

const S = {
  bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
  txt: "#0f172a", muted: "#64748b", input: "#ffffff",
  pri: "#4ecdc4", ok: "#10b981", warn: "#f97316", err: "#ef4444",
};

export default function GoSnelMapsPage() {
  const { currentUser } = useContext(AppContext);
  const navigate = useNavigate();
  // Map is now SVG-based, no Google Maps ref needed
  
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeProgress, setScrapeProgress] = useState(0);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load user's contacts
  useEffect(() => {
    const loadContacts = async () => {
      if (!currentUser) return;
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('id, name, area, district, latitude, longitude, lat, lng')
          .eq('assigned_to', currentUser.id)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setContacts(data || []);
      } catch (err) {
        console.error('Load contacts error:', err);
      }
    };
    loadContacts();
  }, [currentUser]);

  // Handle district selection
  const handleDistrictSelect = (district) => {
    setSelectedDistrict(district);
  };

  // Start scraping restaurants (loads Google Maps API on demand)
  const handleStartScraping = async () => {
    if (!selectedDistrict) return;
    
    setIsScraping(true);
    setScrapeProgress(0);
    setScrapeResult(null);
    
    try {
      setScrapeProgress(10);
      
      // Load Google Maps API on demand for scraping
      const google = await loadGoogleMapsAPI();
      setScrapeProgress(25);
      
      // Create a dummy map element for PlacesService
      const dummyMap = new google.maps.Map(document.createElement('div'));
      const service = new google.maps.places.PlacesService(dummyMap);
      
      const request = {
        query: `مطعم او كافتيريا في ${selectedDistrict.name} عجمان`,
        location: new google.maps.LatLng(selectedDistrict.center.lat, selectedDistrict.center.lng),
        radius: 2000,
        type: 'restaurant'
      };
      
      setScrapeProgress(40);
      
      const results = await new Promise((resolve, reject) => {
        service.textSearch(request, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(results);
          } else {
            reject(new Error('No results found'));
          }
        });
      });
      
      setScrapeProgress(60);
      
      // Parse results
      const restaurants = results
        .filter(r => r.name && r.name.trim())
        .slice(0, 20) // Limit to 20 per scrape
        .map(r => ({
          name: r.name,
          phone: r.formatted_phone_number || '',
          address: r.formatted_address || '',
          district: selectedDistrict.name,
          lat: r.geometry?.location?.lat() || null,
          lng: r.geometry?.location?.lng() || null,
        }));
      
      setScrapeProgress(80);
      
      // Remove duplicates based on phone number
      const { data: existingContacts } = await supabase
        .from('contacts')
        .select('phone')
        .in('phone', restaurants.filter(r => r.phone).map(r => r.phone));
      
      const existingPhones = new Set(existingContacts.map(c => c.phone));
      const newRestaurants = restaurants.filter(r => !r.phone || !existingPhones.has(r.phone));
      
      if (newRestaurants.length === 0) {
        setScrapeResult({
          success: false,
          message: `تم العثور على ${restaurants.length} مطعم، لكن جميعها موجودة بالفعل في جهات الاتصال`,
          count: 0
        });
        setIsScraping(false);
        setScrapeProgress(100);
        return;
      }
      
      // Save to Supabase
      const { data, error } = await supabase
        .from('contacts')
        .insert(newRestaurants.map(r => ({
          name: r.name,
          owner_name: null,
          phone: r.phone || '',
          district: r.district,
          area: r.address || '',
          status: 'new',
          assigned_to_marketer: currentUser.id,
          source: 'gosnel_maps',
          latitude: r.lat,
          longitude: r.lng
        })));
      
      if (error) throw error;
      
      setScrapeProgress(100);
      setScrapeResult({
        success: true,
        message: `✅ تم العثور على ${newRestaurants.length} مطعم جديد في ${selectedDistrict.nameAr || selectedDistrict.name}! تمت إضافتها إلى جهات الاتصال.`,
        count: newRestaurants.length
      });
      
    } catch (error) {
      console.error('Scraping error:', error);
      setScrapeResult({
        success: false,
        message: '❌ حدث خطأ أثناء البحث. يرجى المحاولة مرة أخرى.',
        count: 0
      });
    } finally {
      setIsScraping(false);
    }
  };

  return (
    <div style={{ 
      padding: isMobile ? "12px" : "clamp(16px, 3vw, 32px)", 
      direction: 'rtl',
      minHeight: '100vh',
      background: S.bg
    }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ color: '#000000', fontSize: isMobile ? 22 : 28, fontWeight: 900, marginBottom: 8 }}>
          🗺️ GoSnel Maps
        </h1>
        <p style={{ color: '#64748b', fontSize: isMobile ? 13 : 14 }}>
          اختر منطقة للبحث عن مطاعم وكافتيريات وإضافتها إلى جهات الاتصال
        </p>
      </div>

      {isMobile ? (
        /* Mobile Layout */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Dropdown */}
          <div style={{ background: S.card, borderRadius: 12, padding: 16, border: `1px solid ${S.border}` }}>
            <label style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 8, display: 'block' }}>
              اختر المنطقة:
            </label>
            <select
              value={selectedDistrict?.name || ''}
              onChange={(e) => {
                const district = AJMAN_DISTRICTS.find(d => d.name === e.target.value);
                if (district) handleDistrictSelect(district);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                color: '#000000',
                fontSize: 14,
                fontFamily: "'Cairo',sans-serif",
                outline: 'none'
              }}
            >
              <option value="">-- اختر منطقة --</option>
              {AJMAN_DISTRICTS.sort((a, b) => a.nameAr.localeCompare(b.nameAr)).map(d => (
                <option key={d.name} value={d.name}>
                  {d.nameAr} ({d.name})
                </option>
              ))}
            </select>
          </div>

          {/* Map */}
          <div style={{ 
            height: '400px', 
            borderRadius: 12, 
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <GoSnelMap 
              contacts={contacts}
              selectedDistrict={selectedDistrict}
              onDistrictClick={handleDistrictSelect}
            />
          </div>

          {/* Action Panel */}
          {selectedDistrict && (
            <div style={{ 
              background: '#ffffff', 
              border: '2px solid #4ecdc4', 
              borderRadius: 12, 
              padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                📍 {selectedDistrict.nameAr} - {selectedDistrict.name}
              </h3>
              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
                هل تريد البحث عن المطاعم والكافتيريات في هذه المنطقة وإضافتها إلى جهات الاتصال الخاصة بك؟
              </p>
              
              {!isScraping && !scrapeResult && (
                <button
                  onClick={handleStartScraping}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                    border: 'none',
                    borderRadius: 10,
                    padding: '14px 24px',
                    color: '#ffffff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'Cairo',sans-serif"
                  }}
                >
                  🚀 ابدأ البحث عن المطاعم
                </button>
              )}

              {isScraping && (
                <div>
                  <div style={{ 
                    height: 8, 
                    background: '#e2e8f0', 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    marginBottom: 12
                  }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${scrapeProgress}%`, 
                      background: 'linear-gradient(90deg, #4ecdc4, #38b2ac)',
                      borderRadius: 4,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center' }}>
                    جاري البحث عن المطاعم في {selectedDistrict.nameAr}...
                  </p>
                </div>
              )}

              {scrapeResult && (
                <div style={{ 
                  padding: 16, 
                  borderRadius: 8, 
                  background: scrapeResult.success ? '#f0fdfa' : '#fef2f2',
                  border: `1px solid ${scrapeResult.success ? '#10b981' : '#ef4444'}`
                }}>
                  <p style={{ 
                    color: scrapeResult.success ? '#10b981' : '#ef4444', 
                    fontSize: 14, 
                    fontWeight: 700,
                    textAlign: 'center',
                    marginBottom: 16
                  }}>
                    {scrapeResult.message}
                  </p>
                  {scrapeResult.success && (
                    <button
                      onClick={() => navigate('/contacts')}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: 8,
                        padding: '12px 20px',
                        color: '#ffffff',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: "'Cairo',sans-serif"
                      }}
                    >
                      📋 عرض جهات الاتصال
                    </button>
                  )}
                  <button
                    onClick={() => setScrapeResult(null)}
                    style={{
                      width: '100%',
                      marginTop: 8,
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '10px 20px',
                      color: '#000000',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontFamily: "'Cairo',sans-serif"
                    }}
                  >
                    البحث مرة أخرى
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Desktop Layout */
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Left Panel - District List */}
          <div style={{ 
            width: 320, 
            background: '#ffffff', 
            border: '1px solid #e2e8f0', 
            borderRadius: 12, 
            padding: 20,
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              المناطق في عجمان
            </h2>
            
            {/* District Dropdown */}
            <select
              value={selectedDistrict?.name || ''}
              onChange={(e) => {
                const district = AJMAN_DISTRICTS.find(d => d.name === e.target.value);
                if (district) handleDistrictSelect(district);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                color: '#000000',
                fontSize: 14,
                fontFamily: "'Cairo', sans-serif",
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">-- اختر منطقة --</option>
              {AJMAN_DISTRICTS.sort((a, b) => a.nameAr.localeCompare(b.nameAr)).map(d => (
                <option key={d.name} value={d.name}>
                  {d.nameAr} ({d.name})
                </option>
              ))}
            </select>
          </div>

          {/* Right Panel - Map */}
          <div style={{ flex: 1 }}>
            {/* Map Container */}
            <div style={{ 
              height: '600px', 
              borderRadius: 12, 
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <GoSnelMap 
                contacts={contacts}
                selectedDistrict={selectedDistrict}
                onDistrictClick={handleDistrictSelect}
              />
            </div>

            {/* Selected District Info & Actions */}
            {selectedDistrict && (
              <div style={{ 
                marginTop: 16, 
                background: '#ffffff', 
                border: '2px solid #4ecdc4', 
                borderRadius: 12, 
                padding: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
                  📍 {selectedDistrict.nameAr} - {selectedDistrict.name}
                </h3>
                <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                  هل تريد البحث عن المطاعم والكافتيريات في هذه المنطقة وإضافتها إلى جهات الاتصال الخاصة بك؟
                </p>
                
                {!isScraping && !scrapeResult && (
                  <button
                    onClick={handleStartScraping}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                      border: 'none',
                      borderRadius: 10,
                      padding: '14px 24px',
                      color: '#ffffff',
                      fontSize: 16,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: "'Cairo',sans-serif"
                    }}
                  >
                    🚀 ابدأ البحث عن المطاعم
                  </button>
                )}

                {isScraping && (
                  <div>
                    <div style={{ 
                      height: 8, 
                      background: '#e2e8f0', 
                      borderRadius: 4, 
                      overflow: 'hidden',
                      marginBottom: 12
                    }}>
                      <div style={{ 
                        height: '100%', 
                        width: `${scrapeProgress}%`, 
                        background: 'linear-gradient(90deg, #4ecdc4, #38b2ac)',
                        borderRadius: 4,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center' }}>
                      جاري البحث عن المطاعم في {selectedDistrict.nameAr}...
                    </p>
                  </div>
                )}

                {scrapeResult && (
                  <div style={{ 
                    padding: 16, 
                    borderRadius: 8, 
                    background: scrapeResult.success ? '#f0fdfa' : '#fef2f2',
                    border: `1px solid ${scrapeResult.success ? '#10b981' : '#ef4444'}`
                  }}>
                    <p style={{ 
                      color: scrapeResult.success ? '#10b981' : '#ef4444', 
                      fontSize: 14, 
                      fontWeight: 700,
                      textAlign: 'center',
                      marginBottom: 16
                    }}>
                      {scrapeResult.message}
                    </p>
                    {scrapeResult.success && (
                      <button
                        onClick={() => navigate('/contacts')}
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          border: 'none',
                          borderRadius: 8,
                          padding: '12px 20px',
                          color: '#ffffff',
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: "'Cairo',sans-serif"
                        }}
                      >
                        📋 عرض جهات الاتصال
                      </button>
                    )}
                    <button
                      onClick={() => setScrapeResult(null)}
                      style={{
                        width: '100%',
                        marginTop: 8,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '10px 20px',
                        color: '#000000',
                        fontSize: 14,
                        cursor: 'pointer',
                        fontFamily: "'Cairo',sans-serif"
                      }}
                    >
                      البحث مرة أخرى
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
